import type { UseComboboxPropGetters } from "downshift";
import { useCombobox } from "downshift";
import type { ReactNode } from "react";
import { memo, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { twJoin } from "tailwind-merge";

import chevronDownIcon from "@/assets/icons/chevron-down.svg";
import xCloseIcon from "@/assets/icons/x-close.svg";
import { autoCompleteDisabledProperties } from "@/helpers/auto-complete-helpers";
import { twResolve } from "@/helpers/tw-resolve";
import { useSelectFloating } from "@/ui-adapters/useSelectFloating";

import { fieldControlStateVariants } from "./field-control";

interface SearchableSelectBaseProps<T, TEmptyItem extends React.ReactNode | undefined = undefined> {
  id?: string;
  placeholder?: string;
  selected: T | undefined;
  disabled?: boolean;
  items: T[];
  "aria-invalid"?: boolean;
  searchableFieldSelector: (item: T) => string[];
  renderOption: (item: T, selected: boolean) => ReactNode;
  renderSelected?: (item: T) => ReactNode;
  keySelector: (item: T) => string | number;
  onChange: (item: TEmptyItem extends undefined | null ? T : T | undefined) => void;
  usePortal?: boolean;
  emptyItem?: TEmptyItem;
}

type SearchableSelectClearability =
  | {
      isClearable: true;
      clearLabel: string;
    }
  | {
      isClearable?: false;
      clearLabel?: never;
    };

export type SearchableSelectProps<
  T,
  TEmptyItem extends React.ReactNode | undefined = undefined,
> = SearchableSelectBaseProps<T, TEmptyItem> & SearchableSelectClearability;

/** Renders a typed single-value select whose options can be filtered with free text. */
export function SearchableSelect<T, TEmptyItem extends React.ReactNode | undefined = undefined>({
  id,
  selected,
  disabled,
  placeholder,
  items,
  renderOption,
  renderSelected = (x) => renderOption(x, false),
  searchableFieldSelector,
  keySelector,
  onChange,
  "aria-invalid": isInvalid,
  isClearable,
  clearLabel,
  usePortal = false,
  emptyItem,
}: SearchableSelectProps<T, TEmptyItem>): React.ReactNode {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [inputValue, setInputValue] = useState("");

  const handleClear = () => {
    onChange(undefined as T);
  };

  const filteredItems = useMemo(
    () =>
      items.filter((item) =>
        searchableFieldSelector(item).some((value) => value.toLowerCase().includes(inputValue.toLowerCase())),
      ),
    [inputValue, items, searchableFieldSelector],
  );
  const comboboxItems = useMemo(
    () => [...(emptyItem == null ? [] : [null]), ...filteredItems] as (T | null)[],
    [emptyItem, filteredItems],
  );

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    getLabelProps,
    closeMenu,
  } = useCombobox({
    inputValue,
    defaultHighlightedIndex: 0,
    selectedItem: selected ?? null,
    items: comboboxItems,
    onSelectedItemChange(changes) {
      if (emptyItem == null && !changes.selectedItem) {
        return;
      }
      onChange((changes.selectedItem ?? undefined) as T);
      setInputValue("");
      closeMenu();
      buttonRef.current?.focus();
    },
    onStateChange: ({ inputValue, type }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(inputValue || "");
          break;
        case useCombobox.stateChangeTypes.InputBlur:
          closeMenu();
          buttonRef.current?.focus();
          setInputValue("");
          break;
      }
    },
  });

  const { referenceRef, menuRef, menuProps, opensAbove } = useSelectFloating({
    open: isOpen,
    usePortal,
    menuSize: "searchable-select",
  });

  const menuContent = isOpen ? (
    <>
      {emptyItem != null && (
        <li
          data-testid="searchable-select-item"
          {...getItemProps({ index: 0, item: null })}
          className={twJoin(
            "w-full cursor-pointer px-select-option-inline py-select-option-block text-text-primary italic select-none first:mt-select-menu-edge last:mb-select-menu-edge",
            highlightedIndex === 0 && "bg-background-brand-light",
          )}
        >
          <div className="flex items-center">{emptyItem || "-"}</div>
        </li>
      )}
      {filteredItems.map((item, index) => {
        const adjustedIndex = emptyItem != null ? index + 1 : index;

        return (
          <ListItem<T>
            key={keySelector(item) || `__${index}`}
            renderOption={renderOption}
            item={item}
            isSelected={item === selected}
            index={adjustedIndex}
            isHighlighted={highlightedIndex === adjustedIndex}
            getItemProps={getItemProps}
          />
        );
      })}
    </>
  ) : null;

  const menu = (
    <ul
      {...getMenuProps({ ref: menuRef }, { suppressRefError: !isOpen })}
      {...menuProps}
      data-portal-dropdown={usePortal || undefined}
      className={twJoin(
        "z-select-menu flex w-max cursor-default flex-col items-start overflow-auto rounded-lg border border-border-tertiary bg-background-default outline-hidden!",
        opensAbove ? "rounded-b-none" : "rounded-t-none",
        usePortal ? "shadow-overlay" : "shadow-searchable-select-menu",
      )}
    >
      {menuContent}
    </ul>
  );

  return (
    <div
      className={twJoin("relative", isOpen && "z-20", disabled && "pointer-events-none")}
      data-testid="searchable-select"
    >
      <div>
        <div
          {...getLabelProps()}
          ref={referenceRef}
          className={twJoin(
            "relative flex min-h-control-sm w-full cursor-default flex-wrap items-center border border-border-secondary bg-background-default pr-searchable-select-adornment pl-select-trigger-inline text-left leading-select-trigger",
            disabled && "bg-background-disabled text-text-on-disabled",
            "rounded-lg",
            isOpen && "border-border-brand-secondary",
            isOpen && (opensAbove ? "rounded-t-none" : "rounded-b-none"),
            isClearable && selected && "pr-select-trigger-clear",
            fieldControlStateVariants({ invalid: isInvalid, focus: "within" }),
          )}
        >
          <div className="relative flex w-full flex-1 items-center">
            <div
              className={twJoin(
                "pointer-events-none absolute max-w-full truncate text-text-primary",
                disabled && "text-text-secondary",
              )}
            >
              {selected && !isOpen && !inputValue ? renderSelected(selected) : null}
            </div>
            <input
              {...getInputProps()}
              aria-invalid={isInvalid}
              placeholder={isOpen || !selected ? placeholder : undefined}
              className="h-searchable-select-input w-searchable-select-input-collapsed min-w-searchable-select-input-min flex-1 bg-transparent py-select-option-block text-body placeholder:text-text-tertiary focus:outline-hidden"
              {...autoCompleteDisabledProperties}
            />
          </div>
          <div className="absolute inset-y-0 right-0 flex items-center">
            {isClearable && selected && (
              <button
                aria-label={clearLabel}
                type="button"
                onClick={handleClear}
                className="pointer-events-auto text-text-secondary transition-colors hover:text-text-secondary"
              >
                <SearchableSelectIcon name={xCloseIcon} />
              </button>
            )}
            <button
              // oxlint-disable-next-line react-hooks/refs
              {...getToggleButtonProps({ ref: buttonRef, type: "button", id })}
              aria-label="toggle menu"
              className="flex aspect-square h-full items-center justify-center outline-hidden!"
              data-testid="searchable-select-toggle"
            >
              <span className={twResolve("flex items-center text-text-secondary", disabled && "text-text-on-disabled")}>
                <SearchableSelectIcon name={chevronDownIcon} animateRotation rotate={isOpen} />
              </span>
            </button>
          </div>
        </div>
      </div>

      {isOpen && (usePortal ? createPortal(menu, document.body) : menu)}
    </div>
  );
}

const ListItem = memo(ListItemInner) as typeof ListItemInner;
function ListItemInner<T>({
  item,
  index,
  isHighlighted,
  isSelected,
  getItemProps,
  renderOption,
}: {
  item: T;
  index: number;
  isHighlighted: boolean;
  isSelected: boolean;
  getItemProps: UseComboboxPropGetters<T>["getItemProps"];
  renderOption: SearchableSelectProps<T>["renderOption"];
}) {
  return (
    <li
      data-testid="searchable-select-item"
      {...getItemProps({ index, item })}
      className={twJoin(
        "w-full cursor-pointer px-select-option-inline py-select-option-block text-text-primary select-none first:mt-select-menu-edge last:mb-select-menu-edge",
        isHighlighted && "bg-background-brand-light",
      )}
    >
      <div className="flex items-center">{renderOption(item, isSelected)}</div>
    </li>
  );
}

function SearchableSelectIcon({
  name,
  animateRotation = false,
  rotate = false,
}: {
  name: string;
  animateRotation?: boolean;
  rotate?: boolean;
}): React.ReactNode {
  return (
    <span
      className={twJoin("shrink-0 fill-transparent", animateRotation && "transition-transform", rotate && "rotate-180")}
    >
      <svg aria-hidden="true" width={16} height={16} data-testid={name}>
        <use xlinkHref={`#${name}`} />
      </svg>
    </span>
  );
}
